import { createApiRef, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { MePrefs, Mute, Topic } from './types';

export const notificationPreferencesApiRef = createApiRef<NotificationPreferencesApi>({
  id: 'plugin.notification-preferences.service',
});

export interface NotificationPreferencesApi {
  getMe(): Promise<MePrefs>;
  setEmail(email: string): Promise<MePrefs>;
  getTopics(): Promise<Topic[]>;
  getMutes(): Promise<Mute[]>;
  addMute(topic: string, scope?: string): Promise<Mute>;
  removeMute(id: string): Promise<void>;
}

export class NotificationPreferencesClient implements NotificationPreferencesApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async base() {
    return this.discoveryApi.getBaseUrl('notification-preferences');
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const url = `${await this.base()}${path}`;
    const res = await this.fetchApi.fetch(url, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...init?.headers },
    });
    if (!res.ok) throw new Error(`Notification Preferences API error: ${res.statusText}`);
    if (res.status === 204) return undefined as T;
    return res.json();
  }

  async getMe(): Promise<MePrefs> {
    return this.request<MePrefs>('/me');
  }

  async setEmail(email: string): Promise<MePrefs> {
    return this.request<MePrefs>('/me/email', { method: 'PUT', body: JSON.stringify({ email }) });
  }

  async getTopics(): Promise<Topic[]> {
    const data = await this.request<{ topics: Topic[] }>('/topics');
    return data.topics;
  }

  async getMutes(): Promise<Mute[]> {
    const data = await this.request<{ mutes: Mute[] }>('/mutes');
    return data.mutes;
  }

  async addMute(topic: string, scope?: string): Promise<Mute> {
    const data = await this.request<{ mute: Mute }>('/mutes', {
      method: 'POST',
      body: JSON.stringify({ topic, scope }),
    });
    return data.mute;
  }

  async removeMute(id: string): Promise<void> {
    await this.request<void>(`/mutes/${id}`, { method: 'DELETE' });
  }
}
